import { useMemoizedFn, useMount, useUpdateLayoutEffect } from 'ahooks';
import { Empty, Skeleton } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { Paged } from 'domains/appDomain';
import {
  JobItem,
  JobUIStatusEnum,
  JobWorkflowStatusEnum,
} from 'domains/workspaceDomain';
import { uniqBy } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

interface Props<T> {
  className?: string;
  loadMore: (pageNumber: number) => Promise<Paged<T>>;
  render: ({ items }: { items?: T[] }) => JSX.Element;
  jobStatus?: JobUIStatusEnum | JobWorkflowStatusEnum;
  initialLoad?: boolean;
  threshold?: number;
}

export const RevxJobInfiniteList = ({
  className,
  loadMore,
  render,
  jobStatus,
  initialLoad = true,
  threshold = 200,
}: Props<JobItem>) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [items, setItems] = useState<JobItem[]>();
  const scrollRef = useRef<any>(null);
  const totalPagesRef = useRef<number>(1);

  const loadItems = useCallback(
    async (pageNumber: number) => {
      if (pageNumber < totalPagesRef.current + 1) {
        try {
          setLoading(true);
          const result = await loadMore(pageNumber);
          setTotalItems(result.totalElements);
          totalPagesRef.current = result.totalPages;
          if (jobStatus === JobUIStatusEnum.SAVE_QUOTES) {
            setItems((pre) => [...(pre || []), ...(result.content || [])]);
          } else {
            setItems((pre) =>
              uniqBy([...(pre || []), ...(result.content || [])], 'id')
            );
          }
        } catch (e) {
          setItems([]);
        } finally {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMore, totalPagesRef.current]
  );

  const reset = useMemoizedFn(() => {
    setLoading(false);
    setTotalItems(0);
    totalPagesRef.current = 1;
    setItems([]);
    if (scrollRef.current) {
      scrollRef.current.pageLoaded = 1;
    }
    loadItems(0);
  });

  useUpdateLayoutEffect(() => {
    reset();
  }, [jobStatus]);

  useMount(() => {
    dispatch.workspace.setState({
      resetJobList: reset,
      // updateJobList: updateJobItem,
    });
  });

  const renderDom = useMemo(() => {
    // console.log('Rendering RevxJobInfiniteList with items:', items);
    return React.createElement(render, { items });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <div className={className} style={{ minHeight: 200, overflow: 'auto' }}>
      {Boolean(jobStatus) ? (
        <InfiniteScroll
          ref={scrollRef}
          loadMore={loadItems}
          hasMore={(!items || totalItems > items.length) && !loading}
          threshold={threshold}
          initialLoad={initialLoad}
        >
          {renderDom}
        </InfiniteScroll>
      ) : (
        <></>
      )}
      {Boolean((!items || !items?.length) && !loading) && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 30 }} />
      )}
      {loading && (
        <div className="workspace-item">
          <Skeleton active />
        </div>
      )}
    </div>
  );
};
